import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { validateGeometrySettingsStart } from 'redux/bulkEnquiryLodgement/actions';
import {
  BelGeometrySettings,
  isBufferDistanceValid,
  isMaxSplitAreaValid
} from '../geometrySettings.utils';

const GEOMETRY_VALIDATION_DEBOUNCE_MS = 400;

interface GeometrySettingValues {
  bufferDistance: number;
  maxSplitAreaKm: number;
  applyBuffer: boolean;
  applySplit: boolean;
}

function haveGeometrySettingsChanged(
  previous: GeometrySettingValues,
  current: GeometrySettingValues
): boolean {
  return (
    previous.bufferDistance !== current.bufferDistance ||
    previous.maxSplitAreaKm !== current.maxSplitAreaKm ||
    previous.applyBuffer !== current.applyBuffer ||
    previous.applySplit !== current.applySplit
  );
}

function areLocalGeometryInputsValid(
  geometrySettings: BelGeometrySettings,
  bufferDistance: number,
  maxSplitAreaKm: number,
  applyBuffer: boolean,
  applySplit: boolean
): boolean {
  const bufferActive = geometrySettings.bufferRequired || applyBuffer;
  const splitActive =
    geometrySettings.showSplit &&
    (geometrySettings.splitRequired || applySplit);

  if (
    bufferActive &&
    !isBufferDistanceValid(bufferDistance, geometrySettings)
  ) {
    return false;
  }

  if (splitActive && !isMaxSplitAreaValid(maxSplitAreaKm, geometrySettings)) {
    return false;
  }

  return true;
}

/**
 * Debounces preview calls to POST /enquiries/bulk/{bulkId}/validate-geometry-settings
 * when the user changes buffer or split settings on the Upload step.
 *
 * WHY: Initial validation after upload is dispatched from the upload saga once
 * fileInfo.isValid is set. This hook only re-validates after user edits.
 * HOW: Waits 400ms after the last setting change before dispatching validation.
 */
function useBelGeometryValidation(
  geometrySettings: BelGeometrySettings | null
): void {
  const dispatch = useDispatch();
  const bel = useSelector((state: RootState) => state.belCache);

  const readyForValidation =
    bel.belId != null &&
    bel.fileInfo?.isValid === true &&
    bel.processingPhase === 'IDLE' &&
    !bel.error &&
    bel.validationSummary != null &&
    bel.validationSummary.totalFeatures > 0 &&
    geometrySettings != null;

  const localInputsValid =
    geometrySettings != null &&
    areLocalGeometryInputsValid(
      geometrySettings,
      bel.bufferDistance,
      bel.maxSplitAreaKm,
      bel.applyBuffer,
      bel.applySplit
    );

  const bufferDistance = bel.bufferDistance;
  const maxSplitAreaKm = bel.maxSplitAreaKm;
  const applyBuffer = bel.applyBuffer;
  const applySplit = bel.applySplit;

  const previousSettingsRef = useRef<GeometrySettingValues>({
    bufferDistance,
    maxSplitAreaKm,
    applyBuffer,
    applySplit
  });

  useEffect(() => {
    const currentSettings: GeometrySettingValues = {
      bufferDistance,
      maxSplitAreaKm,
      applyBuffer,
      applySplit
    };
    const previousSettings = previousSettingsRef.current;
    previousSettingsRef.current = currentSettings;

    if (!readyForValidation || !localInputsValid) {
      return undefined;
    }

    if (!haveGeometrySettingsChanged(previousSettings, currentSettings)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      dispatch(validateGeometrySettingsStart({ pendingStepAdvance: false }));
    }, GEOMETRY_VALIDATION_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    dispatch,
    readyForValidation,
    localInputsValid,
    bufferDistance,
    maxSplitAreaKm,
    applyBuffer,
    applySplit
  ]);
}

export default useBelGeometryValidation;
